import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { ChurnSignalsDto, UserChurnScoreDto } from '../../../models/churn-prediction/dto/churn-prediction.dto';
import { translate, TranslationKeys } from '../../../i18n';
import { formatSignalValue, getSignalColorScheme, SignalFormat } from '../utils/churn.utils';
import ChurnTierBadge from './ChurnTierBadge';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: UserChurnScoreDto | null;
}

interface SignalConfig {
  key: keyof ChurnSignalsDto;
  labelKey: string;
  format: SignalFormat;
}

const SIGNAL_KEYS: SignalConfig[] = [
  { key: 'bookingFrequencyDecline', labelKey: 'churn_prediction.signal_booking_decline', format: 'risk' },
  { key: 'daysSinceLastBooking', labelKey: 'churn_prediction.signal_days_since_booking', format: 'risk' },
  { key: 'paymentFailure', labelKey: 'churn_prediction.signal_payment_failure', format: 'binary' },
  { key: 'socialIsolation', labelKey: 'churn_prediction.signal_social_isolation', format: 'risk' },
  { key: 'subscriptionStatus', labelKey: 'churn_prediction.signal_subscription', format: 'subscription' },
  { key: 'cancellationRate', labelKey: 'churn_prediction.signal_cancellation', format: 'risk' },
  { key: 'walletDepletion', labelKey: 'churn_prediction.signal_wallet_depletion', format: 'risk' },
];

export default function ChurnUserSignalModal({ isOpen, onClose, user }: Props) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap="10px">
            <Text color={textColor}>
              {user.firstName} {user.lastName}
            </Text>
            <ChurnTierBadge tier={user.tier} />
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="24px">
          <Flex align="center" justify="center" mb="20px">
            <Box textAlign="center">
              <Text fontSize="4xl" fontWeight="700" color={textColor}>
                {(user.score * 100).toFixed(0)}%
              </Text>
              <Text fontSize="sm" color="gray.500">
                {translate('churn_prediction.overall_score')}
              </Text>
            </Box>
          </Flex>

          {SIGNAL_KEYS.map(({ key, labelKey, format }) => {
            const value = user.signals[key];
            return (
              <Box key={key} mb="14px">
                <Flex justify="space-between" mb="4px">
                  <Text fontSize="sm" color={textColor} fontWeight="500">
                    {translate(labelKey as TranslationKeys)}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {formatSignalValue(value, format)}
                  </Text>
                </Flex>
                <Progress
                  value={value * 100}
                  colorScheme={getSignalColorScheme(value)}
                  size="sm"
                  borderRadius="full"
                />
              </Box>
            );
          })}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
